import { useEffect, useState } from 'react';
import useLazyLoading from '../../common/hooks/loader';
import Loader from '../../components/loader';
import { useNotification } from '../../components/notification/useNotification';
import { IIntegrationPartnerUserDto } from '../../interfaces/integration-partner/IIntegrationPartnerUserDto';
import { IntegrationPartnerEnum, IntegrationPartnerLabel } from '../../common/enum/integration-partner-enum';
import {
    tryCreateIntegrationPartner,
    tryGetIntegrationPartnerById,
    tryUpdateIntegrationPartner
} from '../../services/integration-partner/integration-partner.service';
import extractErrorMessage from '../../common/utils/errorMessages';
import { useFormik } from 'formik';
import {
    Button, FormHelperText, Grid, InputLabel,
    MenuItem, OutlinedInput, Select, Stack, Switch
} from '@mui/material';
import { CreateEditIntegrationValidationSchema } from './create-edit.validation';
import { useNavigate } from 'react-router-dom';
import { getBaseURLWithAuth } from '../../common/utils/session';

interface Props {
    editMode: boolean;
    integrationId?: string;
}

export default function CreateEditIntegrationForm(props: Props) {
    const { editMode, integrationId } = props;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const showLoading = useLazyLoading(loading);
    const { displayNotification } = useNotification();

    const initialValues: IIntegrationPartnerUserDto = {
        id: '00000000-0000-0000-0000-000000000000',
        partner: IntegrationPartnerEnum.CoinGecko,
        apiKey: '',
        isActive: true,
    };

    const formik = useFormik({
        initialValues,
        validationSchema: CreateEditIntegrationValidationSchema(),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setLoading(true);

                if (!editMode) {
                    const response = await tryCreateIntegrationPartner(values);
                    if (response?.status === 201) {
                        displayNotification({ type: 'success', message: 'Integração criada com sucesso' });
                    }
                } else {
                    const response = await tryUpdateIntegrationPartner(values);
                    if (response?.status === 200) {
                        displayNotification({ type: 'success', message: 'Integração actualizada com sucesso' });
                    }
                }
                navigate(getBaseURLWithAuth() + 'integrations');
            } catch (error) {
                displayNotification({ type: 'error', message: extractErrorMessage(error) });
                setSubmitting(false);
            } finally {
                setLoading(false);
            }
        }
    });

    useEffect(() => {
        async function loadIntegration() {
            if (!editMode || !integrationId) return;
            setLoading(true);
            try {
                const response = await tryGetIntegrationPartnerById(integrationId);
                if (response?.data) {
                    updateFormValues(response.data);
                }
            } catch (error) {
                displayNotification({ type: 'error', message: extractErrorMessage(error) });
            } finally {
                setLoading(false);
            }
        }
        loadIntegration();
    }, []);

    function updateFormValues(dto: IIntegrationPartnerUserDto) {
        formik.setFieldValue('id', dto.id);
        formik.setFieldValue('partner', dto.partner);
        formik.setFieldValue('apiKey', dto.apiKey);
        formik.setFieldValue('isActive', dto.isActive);
    }

    return (
        <>
            {showLoading ? <Loader /> : null}
            <form noValidate onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                    <Grid sx={{ flex: 2 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel required sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Parceiro
                            </InputLabel>
                            <Select
                                value={formik.values.partner}
                                onChange={(e) => formik.setFieldValue('partner', Number(e.target.value))}
                                disabled={editMode}
                            >
                                {Object.values(IntegrationPartnerEnum)
                                    .filter((v): v is IntegrationPartnerEnum => typeof v === 'number')
                                    .map((value) => (
                                        <MenuItem key={value} value={value}>
                                            {IntegrationPartnerLabel[value]}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </Stack>
                    </Grid>
                    <Grid sx={{ flex: 4 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel required sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                API Key
                            </InputLabel>
                            <OutlinedInput
                                type="text"
                                value={formik.values.apiKey}
                                onBlur={formik.handleBlur}
                                onChange={(e) => formik.setFieldValue('apiKey', e.target.value)}
                                error={Boolean(formik.touched.apiKey && formik.errors.apiKey)}
                            />
                            {formik.touched.apiKey && formik.errors.apiKey && (
                                <FormHelperText error>{formik.errors.apiKey}</FormHelperText>
                            )}
                        </Stack>
                    </Grid>
                    <Grid sx={{ flex: 1 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Activo
                            </InputLabel>
                            <Switch
                                checked={formik.values.isActive}
                                onChange={() => formik.setFieldValue('isActive', !formik.values.isActive)}
                            />
                        </Stack>
                    </Grid>
                </Grid>
                <Grid mx={3} mb={3}>
                    <Button
                        disableElevation
                        disabled={formik.isSubmitting}
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        color="primary"
                    >
                        Confirmar
                    </Button>
                </Grid>
            </form>
        </>
    );
}
